/**
 * Browser history integration for the SPA. Internal navigation uses pushState
 * with real path URLs (/wizard/plan?plan=…) so the address bar reflects the
 * current screen. Legacy hash URLs (#/wizard/…) are still parsed on load.
 */

export const HISTORY_STATE_FLAG = 'craftmypartyRoute';

const WIZARD_SEGMENT_TO_SCREEN = {
  themes: 'carousel',
  details: 'input',
  generating: 'loadingDetails',
  working: 'loading',
  plan: 'plan'
};

const SCREEN_TO_WIZARD_SEGMENT = {
  carousel: 'themes',
  input: 'details',
  loadingDetails: 'generating',
  loading: 'working',
  plan: 'plan'
};

export function serializeAppRoute({ showLanding, currentView, currentScreen, planId, isExitGuard }) {
  return {
    showLanding: Boolean(showLanding),
    currentView: currentView || 'dashboard',
    currentScreen: currentScreen || 'input',
    planId: planId || null,
    isExitGuard: Boolean(isExitGuard)
  };
}

/** @deprecated Use routeToPath — kept for any external references */
export function routeToHash(route) {
  return routeToPath(route);
}

export function routeToPath(route) {
  const { showLanding, currentView, currentScreen, planId, isExitGuard } = serializeAppRoute(route);
  if (!showLanding) {
    const segment = SCREEN_TO_WIZARD_SEGMENT[currentScreen] || currentScreen;
    const planQuery = planId ? `plan=${encodeURIComponent(planId)}` : '';
    const guardQuery = isExitGuard ? 'guard=true' : '';
    const query = [planQuery, guardQuery].filter(Boolean).join('&');
    return `/wizard/${segment}${query ? '?' + query : ''}`;
  }
  if (!currentView || currentView === 'dashboard') return '/';
  return `/${currentView}`;
}

function getPlanIdFromLocation(loc) {
  const search = new URLSearchParams(loc.search || '');
  const hash = loc.hash || '';
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const hashSearch = new URLSearchParams(hashQuery);
  return search.get('plan') || hashSearch.get('plan') || null;
}

function getExitGuardFromLocation(loc) {
  const search = new URLSearchParams(loc.search || '');
  const hash = loc.hash || '';
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const hashSearch = new URLSearchParams(hashQuery);
  return search.get('guard') === 'true' || hashSearch.get('guard') === 'true';
}

function parseRouteFromPathname(pathname, planFromQuery, isExitGuard = false) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';

  const wizardMatch = path.match(/^\/wizard\/([^/]+)/i);
  if (wizardMatch) {
    const segment = wizardMatch[1].toLowerCase();
    return serializeAppRoute({
      showLanding: false,
      currentView: 'dashboard',
      currentScreen: WIZARD_SEGMENT_TO_SCREEN[segment] || segment,
      planId: planFromQuery,
      isExitGuard
    });
  }

  if (path === '/' || path === '') {
    return serializeAppRoute({
      showLanding: true,
      currentView: 'dashboard',
      currentScreen: 'input',
      planId: planFromQuery,
      isExitGuard
    });
  }

  const viewMatch = path.match(/^\/([^/]+)/i);
  if (viewMatch) {
    return serializeAppRoute({
      showLanding: true,
      currentView: viewMatch[1].toLowerCase(),
      currentScreen: 'input',
      planId: planFromQuery,
      isExitGuard
    });
  }

  return null;
}

function parseRouteFromHash(hash, planFromQuery, isExitGuard = false) {
  const normalized = hash || '#/';

  const wizardMatch = normalized.match(/^#\/wizard\/([^/?]+)/i);
  if (wizardMatch) {
    const segment = wizardMatch[1].toLowerCase();
    return serializeAppRoute({
      showLanding: false,
      currentView: 'dashboard',
      currentScreen: WIZARD_SEGMENT_TO_SCREEN[segment] || segment,
      planId: planFromQuery,
      isExitGuard
    });
  }

  if (normalized === '#/' || normalized === '' || normalized === '#') {
    return serializeAppRoute({
      showLanding: true,
      currentView: 'dashboard',
      currentScreen: 'input',
      planId: planFromQuery,
      isExitGuard
    });
  }

  const viewMatch = normalized.match(/^#\/([^/?]+)/i);
  if (viewMatch) {
    return serializeAppRoute({
      showLanding: true,
      currentView: viewMatch[1].toLowerCase(),
      currentScreen: 'input',
      planId: planFromQuery,
      isExitGuard
    });
  }

  return null;
}

export function parseRouteFromLocation(loc = typeof window !== 'undefined' ? window.location : null) {
  if (!loc) return null;
  const planFromQuery = getPlanIdFromLocation(loc);
  const isExitGuard = getExitGuardFromLocation(loc);
  const pathRoute = parseRouteFromPathname(loc.pathname, planFromQuery, isExitGuard);
  const pathnameHasNamedRoute = /^\/(wizard\/|settings|billing|help|plans|shared|home)/i.test(loc.pathname || '');

  if (pathnameHasNamedRoute && pathRoute) {
    return pathRoute;
  }

  if (loc.hash && loc.hash.length > 1) {
    const hashRoute = parseRouteFromHash(loc.hash, planFromQuery, isExitGuard);
    if (hashRoute) return hashRoute;
  }

  return pathRoute;
}

export function pushAppHistory(route, { replace = false, clearQuery = false } = {}) {
  if (typeof window === 'undefined') return;
  const serialized = serializeAppRoute(route);
  const state = { [HISTORY_STATE_FLAG]: true, route: serialized };
  let path = routeToPath(serialized);

  if (!clearQuery && window.location.search) {
    const existing = new URLSearchParams(window.location.search);
    const pathHasQuery = path.includes('?');
    const pathSearch = pathHasQuery ? new URLSearchParams(path.slice(path.indexOf('?') + 1)) : new URLSearchParams();
    existing.forEach((value, key) => {
      if (key === 'plan' && pathSearch.has('plan')) return;
      if (!pathHasQuery) {
        path += `${path.includes('?') ? '&' : '?'}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    });
  }

  if (replace) {
    window.history.replaceState(state, '', path);
  } else {
    window.history.pushState(state, '', path);
  }
}

export function replaceAppHistoryRoute(route) {
  pushAppHistory(route, { replace: true, clearQuery: true });
}

export function readHistoryRoute(event) {
  if (event?.state?.[HISTORY_STATE_FLAG]?.route) {
    return serializeAppRoute(event.state.route);
  }
  return parseRouteFromLocation();
}

/** Push the last known in-app route before leaving for Stripe (same tab). */
export function pushHistoryBeforeExternalCheckout() {
  if (typeof window === 'undefined') return;
  try {
    const raw = sessionStorage.getItem('craftmyparty_active_session_v1');
    if (raw) {
      const snap = JSON.parse(raw);
      if (snap && typeof snap === 'object') {
        pushAppHistory({
          showLanding: snap.showLanding !== false,
          currentView: snap.currentView || 'dashboard',
          currentScreen: snap.currentScreen || 'input',
          planId: snap.planId || null
        }, { replace: false });
        return;
      }
    }
  } catch (_) {}
  const parsed = parseRouteFromLocation();
  if (parsed) {
    pushAppHistory(parsed, { replace: false });
  }
}
